import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Loader2, BookOpen, CheckCircle2, TrendingUp, Award, Sparkles,
  Plus, Search, Trophy,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import SEO from "@/components/SEO";
import { useCollectorAuth } from "@/context/CollectorAuthContext";
import { VerifyEmailBanner } from "@/pages/collector/VerifyEmailNotice";

import SummaryStatCard          from "@/components/collector/dashboard/SummaryStatCard";
import DashboardAlbumCard       from "@/components/collector/dashboard/DashboardAlbumCard";
import CompletedAlbumCard       from "@/components/collector/dashboard/CompletedAlbumCard";
import SpecialCollectibleCard   from "@/components/collector/dashboard/SpecialCollectibleCard";
import CertificatePreviewCard   from "@/components/collector/dashboard/CertificatePreviewCard";
import CompactOrderCard         from "@/components/collector/dashboard/CompactOrderCard";

// Batch 3 · Phase 1 — Collection-First Dashboard.
// ONE endpoint feeds the entire page (see routers/dashboard.py). The
// previous order-first snapshot component is intentionally gone; its
// order stats are now surfaced through <CompactOrderCard/> at the
// bottom of the page. Batch 4 will extend this file with Want List,
// Achievements, and Badges by reading additional keys from the same
// payload — no new endpoints are anticipated.
export default function DashboardHome() {
  const { collector } = useCollectorAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/collector-auth/dashboard-summary");
        if (!alive) return;
        setData(r.data);
      } catch (e) {
        if (!alive) return;
        setError(formatApiError(e?.response?.data?.detail) || "Unable to load your dashboard.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const firstName = collector?.first_name || collector?.display_name || "Collector";
  // Canonical email-verified check — identical to the one used in
  // DashboardLayout.jsx's sidebar so both surfaces flip in lockstep.
  // `email_verified_at` is written by the verification endpoint at the
  // same moment `account_status` transitions to "active" (see
  // routers/collector_auth.py L238). `get_current_collector` returns
  // the FRESH DB doc on every request, so a page reload after
  // verification always reflects the true state.
  const emailVerified = Boolean(
    collector?.account_status === "active" && collector?.email_verified_at
  );

  return (
    <div className="space-y-10 md:space-y-12 pb-6" data-testid="dashboard-home">
      <SEO title="My Collection · CollectorUp" />

      {/* Verify-email nudge — only rendered while the account is
          genuinely unverified. Passes the email so the resend flow
          knows where the link went. */}
      {!emailVerified && <VerifyEmailBanner email={collector?.email} />}

      {/* ── Welcome + primary CTAs ─────────────────────────────── */}
      <header
        className="relative overflow-hidden rounded-3xl border-2 border-[var(--ink)] bg-white shadow-[8px_8px_0_var(--panini-yellow)]"
        data-testid="dash-welcome"
        aria-label="Welcome back"
      >
        <div className="absolute -top-16 -right-16 h-64 w-64 rounded-full bg-[var(--panini-yellow-soft)]/60" aria-hidden />
        <div className="relative p-6 sm:p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-5 md:gap-8">
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--smoke)] font-bold">
              Welcome back
            </div>
            <h1
              className="font-display text-4xl sm:text-5xl md:text-[3.25rem] leading-[1.05] mt-2 break-words"
              data-testid="dash-greeting"
            >
              {firstName}&apos;s collection
            </h1>
            <p className="text-sm md:text-base text-[var(--ink)]/70 mt-3 max-w-xl leading-relaxed">
              Everything you own, all in one place. Track completion progress and
              keep adding to your library.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <Link
              to="/dashboard/my-collection"
              data-testid="dash-cta-view-collection"
              aria-label="Open My Collection"
              className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[var(--ink)] bg-[var(--panini-red)] text-white px-6 py-3 text-sm font-black uppercase tracking-widest shadow-[4px_4px_0_var(--ink)] hover:-translate-y-0.5 transition-transform focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--panini-yellow)]"
            >
              <BookOpen size={16} aria-hidden /> Open My Collection
            </Link>
            <Link
              to="/dashboard/my-collection"
              data-testid="dash-cta-add"
              state={{ openAdd: true }}
              aria-label="Add an album or collectible to your collection"
              className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[var(--ink)] bg-white px-6 py-3 text-sm font-black uppercase tracking-widest hover:bg-[var(--panini-yellow-soft)] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--panini-yellow)]"
            >
              <Plus size={16} aria-hidden /> Add to Collection
            </Link>
          </div>
        </div>
      </header>

      {loading ? (
        <LoadingBlock />
      ) : error ? (
        <ErrorBlock message={error} />
      ) : data ? (
        <>
          <SummaryStrip summary={data.summary} />
          <ContinueCollecting rows={data.continue_collecting} />
          <CompletedAlbums rows={data.completed_albums} />
          <SpecialCollectibles rows={data.special_collectibles} />
          <CertificatesPreview certs={data.recent_certificates} total={data.summary.certificates} />
          <OrdersSection orders={data.orders} />
        </>
      ) : null}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// Summary strip — Phase C2a canonical 4-tile snapshot.
//   Albums Owned · Completed · In Progress · Certificates
// The customer-facing "Overall Completion" tile was removed here per
// phase C2a §1. The backend still returns `summary.overall_completion`
// for external consumer compatibility.
// ══════════════════════════════════════════════════════════════════
function SummaryStrip({ summary }) {
  return (
    <section aria-label="Collection summary" data-testid="dash-summary">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <SummaryStatCard label="Albums Owned"     value={summary.albums_owned}
                         Icon={BookOpen}      testid="dash-stat-albums-owned" />
        <SummaryStatCard label="Completed"        value={summary.albums_completed}
                         Icon={CheckCircle2}  tone="good" testid="dash-stat-albums-completed" />
        <SummaryStatCard label="In Progress"      value={summary.albums_in_progress ?? 0}
                         Icon={TrendingUp}    tone="accent" testid="dash-stat-albums-in-progress" />
        <SummaryStatCard label="Certificates"     value={summary.certificates}
                         Icon={Award}         testid="dash-stat-certificates" />
      </div>
    </section>
  );
}

// ══════════════════════════════════════════════════════════════════
// Continue Collecting — every in-progress album.
// ══════════════════════════════════════════════════════════════════
function ContinueCollecting({ rows }) {
  const shown = (rows || []).slice(0, 8);
  return (
    <section data-testid="dash-continue" aria-labelledby="dash-continue-h">
      <SectionHeader
        id="dash-continue-h"
        title="Continue Collecting"
        count={rows?.length}
        actionTo="/dashboard/my-collection"
        actionLabel="See all albums"
      />
      {shown.length === 0 ? (
        <PolishedEmptyState
          Icon={BookOpen}
          title="No albums in progress yet"
          hint="Add your first album to start tracking completion."
          ctaTo="/dashboard/my-collection"
          ctaLabel="Add an album"
          tone="accent"
          testid="dash-continue-empty"
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {shown.map((c) => (
            <DashboardAlbumCard key={c.id} card={c} />
          ))}
        </div>
      )}
    </section>
  );
}

// ══════════════════════════════════════════════════════════════════
// Completed Albums.
// ══════════════════════════════════════════════════════════════════
function CompletedAlbums({ rows }) {
  const shown = (rows || []).slice(0, 6);
  return (
    <section data-testid="dash-completed" aria-labelledby="dash-completed-h">
      <SectionHeader
        id="dash-completed-h"
        title="Completed Albums"
        count={rows?.length}
        actionTo="/dashboard/my-collection"
        actionLabel="View library"
      />
      {shown.length === 0 ? (
        <PolishedEmptyState
          Icon={Trophy}
          title="No completed albums yet"
          hint="Complete your first album and it will be celebrated here."
          ctaTo="/dashboard/my-collection"
          ctaLabel="View My Albums"
          tone="good"
          testid="dash-completed-empty"
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {shown.map((c) => (
            <CompletedAlbumCard key={c.id} card={c} />
          ))}
        </div>
      )}
    </section>
  );
}

// ══════════════════════════════════════════════════════════════════
// Special Collectibles.
// ══════════════════════════════════════════════════════════════════
function SpecialCollectibles({ rows }) {
  const shown = (rows || []).slice(0, 6);
  return (
    <section data-testid="dash-special" aria-labelledby="dash-special-h">
      <SectionHeader
        id="dash-special-h"
        title="Special Collectibles"
        count={rows?.length}
      />
      {shown.length === 0 ? (
        <PolishedEmptyState
          Icon={Sparkles}
          title="No special collectibles yet"
          hint="Treasure boxes, premium items, and special collectibles you add will appear here."
          ctaTo="/dashboard/my-collection"
          ctaLabel="Explore Catalog"
          tone="accent"
          testid="dash-special-empty"
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {shown.map((c) => (
            <SpecialCollectibleCard key={c.id} card={c} />
          ))}
        </div>
      )}
    </section>
  );
}

// ══════════════════════════════════════════════════════════════════
// Certificates preview.
// ══════════════════════════════════════════════════════════════════
function CertificatesPreview({ certs, total }) {
  const shown = (certs || []).slice(0, 3);
  return (
    <section data-testid="dash-certificates" aria-labelledby="dash-certificates-h">
      <SectionHeader
        id="dash-certificates-h"
        title="Certificates"
        count={total}
        actionTo="/dashboard/certificates"
        actionLabel="View all certificates"
      />
      {shown.length === 0 ? (
        <PolishedEmptyState
          Icon={Award}
          title="No certificates yet"
          hint="When Gil issues a certificate for a completed album, it appears here."
          tone="gold"
          testid="dash-certificates-empty"
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
          {shown.map((c) => (
            <CertificatePreviewCard key={c.cert_number} cert={c} />
          ))}
        </div>
      )}
    </section>
  );
}

// ══════════════════════════════════════════════════════════════════
// Orders — secondary on this dashboard.
// ══════════════════════════════════════════════════════════════════
function OrdersSection({ orders }) {
  return (
    <section data-testid="dash-orders" aria-labelledby="dash-orders-h">
      <SectionHeader id="dash-orders-h" title="Orders" />
      <CompactOrderCard orders={orders} />
    </section>
  );
}

// ══════════════════════════════════════════════════════════════════
// Shared bits.
// ══════════════════════════════════════════════════════════════════
function SectionHeader({ id, title, count, actionTo, actionLabel }) {
  return (
    <div className="flex items-end justify-between gap-3 mb-4 md:mb-5">
      <div className="min-w-0">
        <h2
          id={id}
          className="font-display text-2xl md:text-3xl leading-tight truncate"
        >
          {title}
          {typeof count === "number" && (
            <span className="ml-2 text-sm md:text-base text-[var(--smoke)] font-normal tabular-nums align-middle">
              ({count})
            </span>
          )}
        </h2>
      </div>
      {actionTo && (
        <Link
          to={actionTo}
          className="text-xs font-bold uppercase tracking-widest text-[var(--smoke)] hover:text-[var(--ink)] inline-flex items-center gap-1 whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--panini-yellow)] rounded"
          aria-label={`${actionLabel} — ${title}`}
        >
          <Search size={12} aria-hidden /> {actionLabel || "View"}
        </Link>
      )}
    </div>
  );
}

function PolishedEmptyState({ Icon, title, hint, ctaTo, ctaLabel, tone = "default", testid }) {
  const wrapTone = {
    default: "bg-white",
    accent:  "bg-[var(--panini-yellow-soft)]/50",
    good:    "bg-emerald-50/70",
    gold:    "bg-gradient-to-br from-[var(--panini-yellow-soft)]/70 to-white",
  }[tone] || "bg-white";
  const iconTone = {
    default: "bg-white border-[var(--ink)]",
    accent:  "bg-white border-[var(--ink)]",
    good:    "bg-emerald-100 border-emerald-800 text-emerald-800",
    gold:    "bg-[var(--panini-yellow)] border-[var(--ink)]",
  }[tone] || "bg-white border-[var(--ink)]";
  return (
    <div
      className={`rounded-2xl border-2 border-[var(--ink)]/70 border-dashed ${wrapTone} p-8 md:p-10 text-center flex flex-col items-center gap-3`}
      data-testid={testid}
      role="region"
      aria-label={title}
    >
      {Icon && (
        <div className={`w-14 h-14 rounded-2xl border-2 ${iconTone} grid place-items-center shadow-[3px_3px_0_var(--ink)]`}>
          <Icon size={22} aria-hidden />
        </div>
      )}
      <div className="font-display text-xl md:text-2xl mt-1">{title}</div>
      {hint && <div className="text-sm text-[var(--ink)]/70 max-w-md">{hint}</div>}
      {ctaTo && (
        <Link
          to={ctaTo}
          className="inline-flex items-center gap-2 mt-2 rounded-full border-2 border-[var(--ink)] bg-[var(--panini-red)] text-white px-5 py-2.5 text-xs font-black uppercase tracking-widest hover:-translate-y-0.5 transition-transform focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--panini-yellow)]"
        >
          <Plus size={14} aria-hidden /> {ctaLabel}
        </Link>
      )}
    </div>
  );
}

function LoadingBlock() {
  return (
    <div className="rounded-2xl border-2 border-[var(--ink)] bg-white p-12 flex items-center justify-center" data-testid="dash-loading">
      <Loader2 className="animate-spin" />
    </div>
  );
}

function ErrorBlock({ message }) {
  return (
    <div className="rounded-2xl border-2 border-[var(--panini-red)] bg-red-50 p-6 text-sm text-red-900" data-testid="dash-error">
      {message}
    </div>
  );
}
